/**
 * Date Utilities for OilWellX
 * Consistent date handling across the application
 * 
 * @version 1.0.0
 * @description Standardized date conversion utilities for production entries
 * 
 * DATE FORMATS:
 * - ISO Date (YYYY-MM-DD): Used in UI, API requests, human-readable
 * - Entry Date ID (YYYYMMDD): Used as Firestore document IDs, lexicographically sortable
 * 
 * BENEFITS OF YYYYMMDD FORMAT:
 * 1. Lexicographic sorting - dates sort correctly as strings
 * 2. Firebase-friendly - no special characters in document IDs
 * 3. Compact - 8 characters vs 10 for ISO date
 * 4. Range queries work naturally with string comparison
 */

/**
 * Convert ISO date string (YYYY-MM-DD) to Entry Date ID (YYYYMMDD)
 * Used when creating Firestore document IDs for production entries
 * 
 * @param isoDate - Date in YYYY-MM-DD format (e.g., "2025-12-10")
 * @returns Date in YYYYMMDD format (e.g., "20251210")
 * 
 * @example
 * isoToEntryDateId("2025-12-10") // Returns "20251210"
 * isoToEntryDateId("2025-01-05") // Returns "20250105"
 */
export function isoToEntryDateId(isoDate: string): string {
  if (!isoDate) {
    throw new Error('isoToEntryDateId: Date string is required');
  }
  
  // Remove hyphens from ISO date format
  const entryDateId = isoDate.replace(/-/g, '');
  
  // Validate format
  if (!/^\d{8}$/.test(entryDateId)) {
    throw new Error(`isoToEntryDateId: Invalid date format. Expected YYYY-MM-DD, got "${isoDate}"`);
  }
  
  return entryDateId;
}

/**
 * Convert Entry Date ID (YYYYMMDD) to ISO date string (YYYY-MM-DD)
 * Used when displaying dates from Firestore document IDs
 * 
 * @param entryDateId - Date in YYYYMMDD format (e.g., "20251210")
 * @returns Date in YYYY-MM-DD format (e.g., "2025-12-10")
 * 
 * @example
 * entryDateIdToIso("20251210") // Returns "2025-12-10"
 * entryDateIdToIso("20250105") // Returns "2025-01-05"
 */
export function entryDateIdToIso(entryDateId: string): string {
  if (!entryDateId) {
    throw new Error('entryDateIdToIso: Entry date ID is required');
  }
  
  // Validate format
  if (!/^\d{8}$/.test(entryDateId)) {
    throw new Error(`entryDateIdToIso: Invalid date ID format. Expected YYYYMMDD, got "${entryDateId}"`);
  }
  
  // Insert hyphens: YYYYMMDD -> YYYY-MM-DD
  const year = entryDateId.substring(0, 4);
  const month = entryDateId.substring(4, 6);
  const day = entryDateId.substring(6, 8);
  
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date as Entry Date ID (YYYYMMDD)
 * Useful for creating today's production entry
 * 
 * @returns Today's date in YYYYMMDD format
 * 
 * @example
 * getTodayEntryDateId() // Returns "20251210" (on December 10, 2025)
 */
export function getTodayEntryDateId(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  
  return `${year}${month}${day}`;
}

/**
 * Get today's date as ISO date string (YYYY-MM-DD)
 * Useful for UI display and API calls
 * 
 * @returns Today's date in YYYY-MM-DD format
 * 
 * @example
 * getTodayIsoDate() // Returns "2025-12-10" (on December 10, 2025)
 */
export function getTodayIsoDate(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Convert a Date object to Entry Date ID (YYYYMMDD)
 * 
 * @param date - JavaScript Date object
 * @returns Date in YYYYMMDD format
 * 
 * @example
 * dateToEntryDateId(new Date(2025, 11, 10)) // Returns "20251210"
 */
export function dateToEntryDateId(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}${month}${day}`;
}

/**
 * Convert a Date object to ISO date string (YYYY-MM-DD)
 * 
 * @param date - JavaScript Date object
 * @returns Date in YYYY-MM-DD format
 * 
 * @example
 * dateToIsoDate(new Date(2025, 11, 10)) // Returns "2025-12-10"
 */
export function dateToIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Parse Entry Date ID (YYYYMMDD) to Date object
 * 
 * @param entryDateId - Date in YYYYMMDD format
 * @returns JavaScript Date object (at midnight local time)
 * 
 * @example
 * parseEntryDateId("20251210") // Returns Date for December 10, 2025
 */
export function parseEntryDateId(entryDateId: string): Date {
  if (!/^\d{8}$/.test(entryDateId)) {
    throw new Error(`parseEntryDateId: Invalid format. Expected YYYYMMDD, got "${entryDateId}"`);
  }
  
  const year = parseInt(entryDateId.substring(0, 4), 10);
  const month = parseInt(entryDateId.substring(4, 6), 10) - 1; // JS months are 0-indexed
  const day = parseInt(entryDateId.substring(6, 8), 10);
  
  return new Date(year, month, day);
}

/**
 * Check if a string is a valid Entry Date ID (YYYYMMDD)
 * 
 * @param value - String to validate
 * @returns True if valid YYYYMMDD format
 */
export function isValidEntryDateId(value: string): boolean {
  if (!/^\d{8}$/.test(value)) {
    return false;
  }
  
  const year = parseInt(value.substring(0, 4), 10);
  const month = parseInt(value.substring(4, 6), 10);
  const day = parseInt(value.substring(6, 8), 10);
  
  // Basic range validation
  if (year < 2000 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  
  return true;
}

/**
 * Check if a string is a valid ISO date (YYYY-MM-DD)
 * 
 * @param value - String to validate
 * @returns True if valid YYYY-MM-DD format
 */
export function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  
  const [year, month, day] = value.split('-').map(Number);
  
  // Basic range validation
  if (year < 2000 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  
  return true;
}

/**
 * Get yesterday's date as Entry Date ID (YYYYMMDD)
 * Useful for comparing with previous day's production
 * 
 * @returns Yesterday's date in YYYYMMDD format
 */
export function getYesterdayEntryDateId(): string {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return dateToEntryDateId(yesterday);
}

/**
 * Get yesterday's date as ISO date string (YYYY-MM-DD)
 * 
 * @returns Yesterday's date in YYYY-MM-DD format
 */
export function getYesterdayIsoDate(): string {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return dateToIsoDate(yesterday);
}

/**
 * Get the previous day from a given ISO date (YYYY-MM-DD)
 * Used for fetching yesterday's entry when comparing production
 * 
 * @param isoDate - Date in YYYY-MM-DD format
 * @returns Previous day in YYYY-MM-DD format
 * 
 * @example
 * getPreviousIsoDate("2025-12-10") // Returns "2025-12-09"
 * getPreviousIsoDate("2025-01-01") // Returns "2024-12-31"
 */
export function getPreviousIsoDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() - 1);
  return dateToIsoDate(date);
}

/**
 * Get days between two ISO dates
 * Useful for determining if entries are consecutive
 * 
 * @param date1 - First date in YYYY-MM-DD format  
 * @param date2 - Second date in YYYY-MM-DD format
 * @returns Number of days between dates (positive if date2 > date1)
 */
export function daysBetweenIsoDates(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = d2.getTime() - d1.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}

// Default export for convenience
export default {
  isoToEntryDateId,
  entryDateIdToIso,
  getTodayEntryDateId,
  getTodayIsoDate,
  dateToEntryDateId,
  dateToIsoDate,
  parseEntryDateId,
  isValidEntryDateId,
  isValidIsoDate,
  getYesterdayEntryDateId,
  getYesterdayIsoDate,
  getPreviousIsoDate,
  daysBetweenIsoDates,
};
